import { ModalRef } from '../components/modal/modal-ref';

export class ScreenReaderService {
  static contentVisible(ariaHiddenElements: Map<Element, string>, previouslyFocusedElements?: Array<HTMLElement>, document?: Document): void {
    ariaHiddenElements.forEach((previousValue: string, element: Element): void => {
      if ((previousValue ?? null) !== null) {
        element.setAttribute('aria-hidden', previousValue);
      } else {
        element.removeAttribute('aria-hidden');
      }
      // only remove inert when previouslyFocusedElement is provided, which is currently only modal service
      if (previouslyFocusedElements !== undefined) {
        element.removeAttribute('inert');
      }
    });
    ariaHiddenElements.clear();

    if (previouslyFocusedElements !== undefined && document !== undefined) {
      const arrayLength = previouslyFocusedElements.length;
      if (arrayLength === 0) {
        return;
      }
      // Going reverse order due to nested modals, where the most recently focused element is the one that should be focused
      for (let i = arrayLength - 1 ; i >= 0; i--) {
        const element = previouslyFocusedElements[i];
        if (element === undefined || element === null) {
          // If the element is undefined or null, remove it from the array, shouldn't happen but good to check
          previouslyFocusedElements.splice(i, 1);
        } else if (document?.body?.contains?.(element) && element.checkVisibility({ opacityProperty: true })) {
          // Needed to check for visibility due to opacity being set to 0 for modals that are disappearing but are still in the process of being removed
          element?.focus?.();
          // If we successfully found a focusable element, remove it from the array and stop going through the loop
          previouslyFocusedElements.splice(i, 1);
          return;
        } else {
          // If the element is not in the document, or not visible, remove it from the array
          previouslyFocusedElements.splice(i, 1);
        }
      }
    }
  }

  static contentHidden(overlayContainer: HTMLElement, ariaHiddenElements: Map<Element, string>, previouslyFocusedElements?: Array<HTMLElement>, document?: Document, topModal?: ModalRef<undefined>): Array<HTMLElement> | undefined {
    if ((overlayContainer.parentElement ?? null) !== null) {
      const siblings = overlayContainer.parentElement.children;

      const previouslyFocusedElement = document?.activeElement as HTMLElement;
      if (previouslyFocusedElement) {
        previouslyFocusedElement?.blur?.();
        previouslyFocusedElements?.push(previouslyFocusedElement);
      }

      for (let i = siblings.length - 1; i > -1; i--) {
        const sibling = siblings[i];

        if (sibling !== overlayContainer &&
          !sibling.classList.contains('cdk-overlay-container') &&
          !sibling.classList.contains('td-wb-modal-page-scroll-item') &&
          sibling.nodeName !== 'SCRIPT' &&
          sibling.nodeName !== 'STYLE' &&
          sibling.nodeName !== 'CHAT-DOCK' &&
          sibling.nodeName !== 'CHAT-BUTTON' &&
          !sibling.hasAttribute('aria-live') &&
          sibling.id !== 'onetrust-consent-sdk') {

          ariaHiddenElements.set(sibling, sibling.getAttribute('aria-hidden'));
          sibling.setAttribute('aria-hidden', 'true');

          // only set inert when document is provided, which is currently only modal service
          if(document !== undefined) {
            sibling.setAttribute('inert', '');
          }
        }

        this.hideModalSiblings(sibling, ariaHiddenElements, topModal, document !== undefined);
      }
      return previouslyFocusedElements;
    }
  }

  private static hideModalSiblings(sibling: Element, ariaHiddenElements: Map<Element, string>, topModal?: ModalRef<undefined>, setInert?: boolean): void {
    if (topModal && sibling.classList.contains('td-wb-modal-container')) {
      const modalSiblings = sibling.children;
      for (let i = modalSiblings.length - 1; i > -1; i--) {
        const modalSibling = modalSiblings[i];

        const topModalWrapper = topModal.getOverlayElement().parentElement;
        if (modalSibling !== topModalWrapper) {

          ariaHiddenElements.set(modalSibling, modalSibling.getAttribute('aria-hidden'));
          modalSibling.setAttribute('aria-hidden', 'true');
          if(setInert) {
            modalSibling.setAttribute('inert', '');
          }
        } else {
          modalSibling.removeAttribute('aria-hidden');
          if(setInert) {
            modalSibling.removeAttribute('inert');
          }
        }
      }
    }
  }
}
